import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Wrapper } from '@googlemaps/react-wrapper';
import { Icon } from 'semantic-ui-react';
import classNames from 'classnames';

import styles from './MapDisplay.module.scss';

// Google Maps API Key (should be in environment variables)
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

// Debug: Log the API key status (without exposing the actual key)
console.log('Google Maps API Key status:', {
  hasKey: !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  keyLength: process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.length || 0,
  usingFallback: GOOGLE_MAPS_API_KEY === 'YOUR_API_KEY_HERE',
});

// Map component that renders the actual Google Map
const Map = ({ center, zoom, markers, polygons, lines, style, mapOptions }) => {
  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapRef = useCallback(
    node => {
      if (node !== null && !map && node instanceof Element) {
        const newMap = new window.google.maps.Map(node, {
          center,
          zoom,
          mapTypeId: 'roadmap', // Always use roadmap
          tilt: 0, // Disable 3D tilt
          ...mapOptions,
        });
        setMap(newMap);
        setMapLoaded(true);
      }
    },
    [center, zoom, mapOptions, map]
  );

  // Add markers when map is loaded
  React.useEffect(() => {
    if (mapLoaded && map && markers) {
      markers.forEach(markerData => {
        const marker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lng },
          map,
          title: markerData.label || '',
        });

        if (markerData.label) {
          const infoWindow = new window.google.maps.InfoWindow({
            content: markerData.label,
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });
        }
      });
    }
  }, [mapLoaded, map, markers]);

  // Add polygons when map is loaded
  React.useEffect(() => {
    if (mapLoaded && map && polygons) {
      polygons.forEach(polygonData => {
        const polygon = new window.google.maps.Polygon({
          paths: polygonData.coordinates.map(coord => ({ lat: coord[0], lng: coord[1] })),
          strokeColor: polygonData.color || '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: polygonData.color || '#FF0000',
          fillOpacity: 0.35,
        });
        polygon.setMap(map);

        if (polygonData.label) {
          const infoWindow = new window.google.maps.InfoWindow({
            content: polygonData.label,
            position: polygon.getPath().getAt(0),
          });

          polygon.addListener('click', () => {
            infoWindow.open(map);
          });
        }
      });
    }
  }, [mapLoaded, map, polygons]);

  // Add lines when map is loaded
  React.useEffect(() => {
    if (mapLoaded && map && lines) {
      lines.forEach(lineData => {
        const polyline = new window.google.maps.Polyline({
          path: lineData.coordinates.map(coord => ({ lat: coord[0], lng: coord[1] })),
          geodesic: true,
          strokeColor: lineData.color || '#0000FF',
          strokeOpacity: 1.0,
          strokeWeight: 3,
        });
        polyline.setMap(map);

        if (lineData.label) {
          const infoWindow = new window.google.maps.InfoWindow({
            content: lineData.label,
            position: polyline.getPath().getAt(0),
          });

          polyline.addListener('click', () => {
            infoWindow.open(map);
          });
        }
      });
    }
  }, [mapLoaded, map, lines]);

  return <div ref={mapRef} style={style} className={styles.mapContainer} />;
};

Map.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  zoom: PropTypes.number,
  markers: PropTypes.arrayOf(PropTypes.object),
  polygons: PropTypes.arrayOf(PropTypes.object),
  lines: PropTypes.arrayOf(PropTypes.object),
  style: PropTypes.object,
  mapOptions: PropTypes.object,
};

Map.defaultProps = {
  zoom: 15,
  markers: [],
  polygons: [],
  lines: [],
  style: { height: '200px', width: '100%' },
  mapOptions: {},
};

// Main MapDisplay component
const MapDisplay = ({ mapData, isModal = false, className }) => {
  if (!mapData || !mapData.coordinates) {
    return null;
  }

  // Temporary: Disable Google Maps if no API key is present or if it's the placeholder
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_API_KEY_HERE') {
    const { address, coordinates } = mapData;
    return (
      <div className={classNames(styles.mapWrapper, className)}>
        <div className={styles.mapPlaceholder} style={{ height: isModal ? '400px' : '200px' }}>
          <Icon name="map" size="huge" />
          <p>Map visualization is disabled.</p>
          {address && <p>{address}</p>}
          {coordinates && (
            <small>
              Lat: {coordinates.lat}, Lng: {coordinates.lng}
            </small>
          )}
        </div>
      </div>
    );
  }

  const {
    coordinates,
    address,
    markers = [],
    polygons = [],
    lines = [],
    zoom = 15,
    style: mapStyle = 'roadmap',
    lastUpdate,
    status,
  } = mapData;

  const mapHeight = isModal ? '400px' : '200px';
  const mapOptions = {
    mapTypeId: mapStyle,
    disableDefaultUI: !isModal,
    zoomControl: isModal,
    streetViewControl: isModal,
    fullscreenControl: isModal,
  };

  const render = status => {
    if (status === 'LOADING') {
      return (
        <div className={classNames(styles.mapPlaceholder, className)}>
          <Icon name="spinner" loading />
          <span>Loading map...</span>
        </div>
      );
    }

    if (status === 'FAILURE') {
      return (
        <div className={classNames(styles.mapError, className)}>
          <Icon name="exclamation triangle" />
          <span>Failed to load map</span>
        </div>
      );
    }

    return (
      <div className={classNames(styles.mapWrapper, className)}>
        <Map
          center={coordinates}
          zoom={zoom}
          markers={markers}
          polygons={polygons}
          lines={lines}
          style={{ height: mapHeight, width: '100%' }}
          mapOptions={mapOptions}
        />
        {(address || status || lastUpdate) && (
          <div className={styles.mapInfo}>
            {address && (
              <div className={styles.mapAddress}>
                <Icon name="map marker alternate" />
                <span>{address}</span>
              </div>
            )}
            {status && (
              <div className={styles.mapStatus}>
                <Icon name="info circle" />
                <span>Status: {status}</span>
              </div>
            )}
            {lastUpdate && (
              <div className={styles.mapUpdate}>
                <Icon name="clock outline" />
                <span>Updated: {new Date(lastUpdate).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} libraries={['places']} />;
};

MapDisplay.propTypes = {
  mapData: PropTypes.shape({
    coordinates: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    address: PropTypes.string,
    markers: PropTypes.arrayOf(PropTypes.object),
    polygons: PropTypes.arrayOf(PropTypes.object),
    lines: PropTypes.arrayOf(PropTypes.object),
    zoom: PropTypes.number,
    style: PropTypes.string,
    lastUpdate: PropTypes.string,
    status: PropTypes.string,
  }),
  isModal: PropTypes.bool,
  className: PropTypes.string,
};

MapDisplay.defaultProps = {
  mapData: null,
  isModal: false,
  className: undefined,
};

export default MapDisplay;
